import {
  BASE_PATH,
  NAME_MAX_LENGTH,
  PLAYER_COLORS,
  rank,
  sanitizeName,
  type BoardView,
  type Difficulty,
  type ErrorCode,
  type PlayerInfo,
  type RoomStatus,
  type RoundSummary,
  type ServerMsg,
} from "../src/protocol";
import { track } from "./analytics";
import { openDialog } from "./dialog";
import { h } from "./dom";
import { brandMark, siteFooter } from "./home";
import { copyIcon, exitIcon, shareIcon } from "./icons";
import { uiLangSelect } from "./langPicker";
import { Connection, type ConnectionStatus } from "./net";
import { qrSvg } from "./qr";
import { shareButton, type ResultCard } from "./shareCard";
import { getName, getPlayerId, setName } from "./storage";
import { STRINGS } from "./strings";
import { formatClock } from "./timing";

const CANONICAL_ORIGIN = "https://workbrawl.com";
const TOAST_MS = 2500;
/** The clock is green until this much time is left, then shifts gradually towards red… */
const CLOCK_WARN_MS = 30_000;
/** …which it reaches here: the last seconds are solid red and blink. */
const CLOCK_FINAL_MS = 10_000;
const CLOCK_GREEN_HUE = 135;
/** How often the clock refreshes. The server sends no ticks. */
const REFRESH_MS = 100;
/** Holding a finger on a cell this long places a flag instead of opening it. */
const LONG_PRESS_MS = 420;

/** Room screen. Asks for a name first when the visitor arrived by link without one. */
export function renderRoom(root: HTMLElement, code: string): void {
  const name = sanitizeName(getName());
  if (name === null) renderNamePrompt(root, code);
  else new RoomScreen(root, code, name);
}

function renderNamePrompt(root: HTMLElement, code: string): void {
  const input = h("input", {
    id: "name",
    type: "text",
    autocomplete: "nickname",
    maxlength: NAME_MAX_LENGTH,
    placeholder: STRINGS.namePlaceholder,
    required: true,
  });
  const error = h("p", { class: "form-error", role: "alert" });
  const form = h(
    "form",
    { class: "card name-prompt", novalidate: true },
    h("h2", {}, STRINGS.namePromptTitle),
    h("p", { class: "muted" }, STRINGS.namePromptBody),
    h("label", { for: "name" }, STRINGS.nameLabel),
    input,
    h("button", { type: "submit", class: "btn btn-primary" }, STRINGS.enterRoom),
    error,
  );
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const name = sanitizeName(input.value);
    if (name === null) {
      error.textContent = STRINGS.nameInvalid;
      input.focus();
      return;
    }
    setName(name);
    new RoomScreen(root, code, name);
  });
  root.replaceChildren(
    h("main", { class: "center-screen" }, h("h1", { class: "brand brand-large" }, brandMark()), form),
    siteFooter(),
  );
  input.focus();
}

class RoomScreen {
  private readonly connection: Connection;
  private readonly toastEl = h("div", { class: "toast", role: "status", "aria-live": "polite", hidden: true });
  private readonly clockEl = h("strong", { class: "clock", "aria-label": STRINGS.timeLeft }, "0:00");
  private readonly headingEl = h("span", { class: "muted" }, STRINGS.timeLeft);
  private readonly livesEl = h("span", { class: "stage-lives" });
  private readonly minesEl = h("span", { class: "stage-mines" });
  private readonly statusEl = h("strong", { class: "stage-status", "aria-live": "polite" });
  private readonly flagButton = h("button", { type: "button", class: "btn btn-small flag-toggle", "aria-pressed": "false" });
  private readonly boardEl = h("div", { class: "minefield", role: "grid", "aria-label": STRINGS.boardLabel });
  private readonly overlayEl = h("div", { class: "arena-overlay" });
  private readonly teamScoreEl = h("strong", { class: "team-score" }, "0");
  private readonly teamStatsEl = h("p", { class: "muted team-stats" });
  private readonly playersEl = h("ol", { class: "player-list" });

  private you = "";
  private status: RoomStatus = "lobby";
  private difficulty: Difficulty = "medium";
  private players = new Map<string, PlayerInfo>();
  private summary: RoundSummary | null = null;
  /** Round length of the room, for the shared result. */
  private roundSeconds = 0;
  private board: BoardView | null = null;
  /** Local time at which the countdown or the round ends. */
  private deadline = 0;
  private refreshTimer: ReturnType<typeof setInterval> | undefined;
  private toastTimer: ReturnType<typeof setTimeout> | undefined;
  /** One button per cell, rebuilt only when the board size changes. */
  private cells: HTMLButtonElement[] = [];
  private cellsFor = "";
  /** When on, a tap places a flag instead of opening the cell. */
  private flagMode = false;
  /** Cell whose long press already placed a flag: the click that follows must not open it. */
  private pressed = -1;
  private fatal = false;

  constructor(
    private readonly root: HTMLElement,
    private readonly code: string,
    name: string,
  ) {
    this.flagButton.addEventListener("click", () => this.setFlagMode(!this.flagMode));
    this.setFlagMode(false);
    const stage = h(
      "div",
      { class: "card stage" },
      h(
        "div",
        { class: "stage-bar" },
        h("div", { class: "stage-counts" }, this.livesEl, this.minesEl),
        this.statusEl,
        this.flagButton,
      ),
      this.boardEl,
      h("p", { class: "muted arena-hint" }, STRINGS.flagHint),
      this.overlayEl,
    );

    root.replaceChildren(
      this.header(),
      this.toastEl,
      h(
        "main",
        { class: "arena-layout" },
        h("section", { class: "arena-area" }, h("div", { class: "arena-hud" }, this.headingEl, this.clockEl), stage),
        h(
          "aside",
          { class: "panel" },
          h(
            "section",
            { class: "card team-card" },
            h("h3", { class: "panel-title" }, STRINGS.teamScore),
            this.teamScoreEl,
            this.teamStatsEl,
          ),
          h("section", { class: "card" }, h("h3", { class: "panel-title" }, STRINGS.contributions), this.playersEl),
          this.inviteCard(),
        ),
      ),
      siteFooter(),
    );

    this.connection = new Connection(Connection.roomUrl(code, getPlayerId(), name), {
      onMessage: (msg) => this.onMessage(msg),
      onStatus: (status) => this.onStatus(status),
    });
    this.connection.connect();
  }

  private header(): HTMLElement {
    const exit = h(
      "button",
      { type: "button", class: "btn btn-small btn-exit", "aria-haspopup": "dialog", "aria-label": STRINGS.exit },
      exitIcon(),
      // Hidden on narrow screens, where the button is icon-only.
      h("span", { class: "btn-label" }, STRINGS.exit),
    );
    exit.addEventListener("click", () => this.confirmExit());
    return h(
      "header",
      { class: "topbar" },
      h("a", { href: `${BASE_PATH}/`, class: "brand" }, brandMark()),
      h(
        "div",
        { class: "room-info" },
        h("span", { class: "muted room-label" }, STRINGS.room),
        h("strong", { class: "room-code" }, this.code),
        uiLangSelect(true),
        exit,
      ),
    );
  }

  private confirmExit(): void {
    const cancel = h("button", { type: "button", class: "btn" }, STRINGS.cancel);
    const leave = h("button", { type: "button", class: "btn btn-danger" }, exitIcon(), STRINGS.exit);
    const dialog = openDialog(
      "confirm",
      h("h2", { class: "dialog-title" }, STRINGS.exitTitle),
      h("p", { class: "muted" }, STRINGS.exitBody),
      h("div", { class: "dialog-actions" }, cancel, leave),
    );
    cancel.addEventListener("click", () => dialog.close());
    leave.addEventListener("click", () => {
      // Suppress the "connection lost" message while we leave on purpose.
      this.fatal = true;
      this.connection.stop();
      location.assign(`${BASE_PATH}/`);
    });
    cancel.focus();
  }

  /** Copies the invite link; reports through a floating message unless `onDone` handles it. */
  private async copyInvite(onDone?: (ok: boolean) => void): Promise<void> {
    const url = inviteUrl(this.code);
    const ok = await copyText(url);
    if (onDone) onDone(ok);
    else this.toast(ok ? STRINGS.linkCopiedToast : `${STRINGS.copyFailed} ${url}`);
  }

  /** Invite card under the scoreboard: title with share/copy actions on the right. */
  private inviteCard(): HTMLElement {
    const share = h(
      "button",
      { type: "button", class: "icon-btn", title: STRINGS.share, "aria-label": STRINGS.share, "aria-haspopup": "dialog" },
      shareIcon(),
    );
    share.addEventListener("click", () => this.openInvite());
    const copy = h(
      "button",
      { type: "button", class: "icon-btn", title: STRINGS.copyLink, "aria-label": STRINGS.copyLink },
      copyIcon(),
    );
    copy.addEventListener("click", () => this.copyInvite());

    return h(
      "section",
      { class: "card invite-card" },
      h(
        "div",
        { class: "invite-head" },
        h("h3", { class: "panel-title" }, STRINGS.inviteTitle),
        h("div", { class: "icon-actions" }, share, copy),
      ),
    );
  }

  /** Modal with a large QR, the link, copy and (where supported) the native share sheet. */
  private openInvite(): void {
    const url = inviteUrl(this.code);
    const copy = h("button", { type: "button", class: "btn" }, copyIcon(), STRINGS.copyLink);
    copy.addEventListener("click", () =>
      this.copyInvite((ok) => {
        copy.replaceChildren(copyIcon(), ok ? STRINGS.linkCopied : STRINGS.copyFailedShort);
        setTimeout(() => copy.replaceChildren(copyIcon(), STRINGS.copyLink), 1500);
      }),
    );
    const close = h("button", { type: "button", class: "btn" }, STRINGS.close);

    const actions: HTMLElement[] = [];
    if (typeof navigator.share === "function") {
      const native = h("button", { type: "button", class: "btn btn-primary" }, shareIcon(), STRINGS.share);
      native.addEventListener("click", () => {
        navigator.share({ title: STRINGS.appName, text: STRINGS.shareText, url }).catch(() => {
          // The share sheet was dismissed: nothing to do.
        });
      });
      actions.push(native);
    }
    actions.push(copy, close);

    const dialog = openDialog(
      "invite",
      h("h2", { class: "dialog-title" }, STRINGS.inviteTitle),
      h("div", { class: "qr-box qr-box-large" }, qrSvg(url, STRINGS.qrLabel(this.code))),
      h("p", { class: "invite-url" }, url),
      h("div", { class: "dialog-actions" }, ...actions),
    );
    close.addEventListener("click", () => dialog.close());
  }

  private setFlagMode(on: boolean): void {
    this.flagMode = on;
    this.flagButton.setAttribute("aria-pressed", String(on));
    this.flagButton.textContent = `\u{1F6A9} ${on ? STRINGS.flagModeOn : STRINGS.flagMode}`;
    this.flagButton.classList.toggle("is-on", on);
    this.boardEl.classList.toggle("is-flagging", on);
  }

  private canAct(): boolean {
    return this.status === "running" && this.board?.phase === "open";
  }

  private reveal(cell: number): void {
    if (this.canAct()) this.connection.send({ type: "reveal", cell });
  }

  private flag(cell: number): void {
    if (this.canAct()) this.connection.send({ type: "flag", cell });
  }

  /** Buttons for a width × height board, with tap, right-click and long-press handling. */
  private buildCells(width: number, height: number): void {
    const key = `${width}x${height}`;
    if (this.cellsFor === key) return;
    this.cellsFor = key;
    this.boardEl.style.setProperty("--cols", String(width));
    this.cells = Array.from({ length: width * height }, (_, cell) => {
      const button = h("button", { type: "button", class: "cell", role: "gridcell" });
      let timer: ReturnType<typeof setTimeout> | undefined;
      const cancel = () => clearTimeout(timer);
      button.addEventListener("pointerdown", (event) => {
        this.pressed = -1;
        if (event.pointerType === "mouse") return;
        cancel();
        timer = setTimeout(() => {
          this.pressed = cell;
          navigator.vibrate?.(20);
          this.flag(cell);
        }, LONG_PRESS_MS);
      });
      button.addEventListener("pointerup", cancel);
      button.addEventListener("pointerleave", cancel);
      button.addEventListener("pointercancel", cancel);
      button.addEventListener("contextmenu", (event) => {
        event.preventDefault();
        // On touch devices the long press above already handled it.
        if (this.pressed !== cell) this.flag(cell);
      });
      button.addEventListener("click", () => {
        if (this.pressed === cell) {
          this.pressed = -1;
          return;
        }
        if (this.flagMode) this.flag(cell);
        else this.reveal(cell);
      });
      return button;
    });
    this.boardEl.replaceChildren(...this.cells);
  }

  private onStatus(status: ConnectionStatus): void {
    if (this.fatal) return;
    if (status === "connecting") this.toast(STRINGS.connecting, true);
    else if (status === "reconnecting") this.toast(STRINGS.reconnecting, true);
    else if (status === "open") this.hideToast();
  }

  private onMessage(msg: ServerMsg): void {
    switch (msg.type) {
      case "state": {
        const previous = this.status;
        this.you = msg.you;
        this.status = msg.status;
        this.roundSeconds = msg.duration;
        // One per person per round: this is what "how much is each title used" is measured by.
        if (msg.status === "running" && previous !== "running") {
          track("round_started", { people: msg.players.length, duration: msg.duration });
        } else if (msg.status === "finished" && previous === "running") {
          track("round_finished", { people: msg.players.length });
        }
        this.difficulty = msg.difficulty;
        this.players = new Map(msg.players.map((p) => [p.id, p]));
        this.summary = msg.summary;
        this.deadline = Date.now() + msg.remainingMs;
        if (msg.status !== "running") this.board = null;
        this.renderStatus();
        this.renderBoard();
        this.renderTeam();
        break;
      }
      case "board": {
        for (const [slot, score] of msg.p) {
          for (const player of this.players.values()) if (player.slot === slot) player.score = score;
        }
        // A mine that someone just set off: a short buzz for everyone.
        if (this.board && this.board.n === msg.n && msg.boom.length > this.board.boom.length) navigator.vibrate?.(60);
        this.board = msg;
        this.deadline = Date.now() + msg.t;
        this.renderBoard();
        this.renderTeam();
        this.refresh();
        break;
      }
      case "player_joined":
      case "player_updated":
        this.players.set(msg.player.id, msg.player);
        this.renderTeam();
        if (this.status === "lobby") this.renderStatus();
        break;
      case "player_left":
        this.players.delete(msg.id);
        this.renderTeam();
        if (this.status === "lobby") this.renderStatus();
        break;
      case "error":
        this.onError(msg.code);
        break;
      case "pong":
        break;
    }
  }

  private onError(code: ErrorCode): void {
    if (code === "BAD_MESSAGE") return;
    this.fatal = true;
    this.connection.stop();
    clearInterval(this.refreshTimer);
    this.root.replaceChildren(
      h(
        "main",
        { class: "center-screen" },
        h("h1", { class: "brand brand-large" }, brandMark()),
        h(
          "section",
          { class: "card center-card" },
          h("p", {}, code === "ROOM_FULL" ? STRINGS.errorRoomFull : STRINGS.errorRoomNotFound),
          h("a", { href: `${BASE_PATH}/`, class: "btn btn-primary" }, STRINGS.backHome),
        ),
      ),
      siteFooter(),
    );
  }

  /** Overlay and timers for the current phase of the room. */
  private renderStatus(): void {
    clearInterval(this.refreshTimer);
    this.refreshTimer = undefined;
    this.overlayEl.hidden = this.status === "running";

    if (this.status === "lobby") {
      this.setClock(0);
      this.overlayEl.replaceChildren(
        h("h2", {}, STRINGS.lobbyTitle),
        h("p", {}, STRINGS.lobbyBody(this.onlineCount())),
        this.startButton(STRINGS.startRound),
      );
    } else if (this.status === "countdown") {
      const number = h("strong", { class: "countdown" });
      const update = () => {
        const next = String(Math.max(1, Math.ceil((this.deadline - Date.now()) / 1000)));
        if (number.textContent === next) return;
        number.textContent = next;
        // Restart the zoom-out for each new number (reading offsetWidth forces a reflow).
        number.classList.remove("countdown-pop");
        void number.offsetWidth;
        number.classList.add("countdown-pop");
      };
      update();
      this.refreshTimer = setInterval(update, REFRESH_MS);
      this.overlayEl.replaceChildren(h("p", {}, STRINGS.getReady), number);
    } else if (this.status === "finished") {
      this.setClock(0);
      this.overlayEl.replaceChildren(...this.resultsContent());
    } else {
      this.refresh();
      this.refreshTimer = setInterval(() => this.refresh(), REFRESH_MS);
    }
  }

  private resultsContent(): HTMLElement[] {
    const summary = this.summary;
    const content: HTMLElement[] = [h("h2", {}, STRINGS.resultsTitle)];
    if (summary) {
      content.push(
        h("p", { class: "results-headline" }, STRINGS.resultsScore(summary.score)),
        h("p", { class: "muted" }, `${STRINGS.clearedCount(summary.cleared)} · ${STRINGS.lostCount(summary.lost)}`),
      );
    }
    content.push(this.startButton(STRINGS.newRound));
    if (summary) content.push(shareButton(() => this.resultCard(summary)));
    return content;
  }

  /** What "Share result" draws: the team's score. */
  private resultCard(summary: RoundSummary): ResultCard {
    return {
      title: "MinesBrawl",
      slug: "minesbrawl",
      accent: "#1c7ed6",
      team: {
        score: STRINGS.points(summary.score),
        detail: `${STRINGS.clearedCount(summary.cleared)} · ${STRINGS.lostCount(summary.lost)}`,
      },
      people: this.players.size,
      minutes: Math.round(this.roundSeconds / 60),
    };
  }

  private startButton(label: string): HTMLElement {
    const button = h("button", { type: "button", class: "btn btn-primary" }, label);
    button.addEventListener("click", () => this.connection.send({ type: "start" }));
    return button;
  }

  /** Lives, mines left and every cell of the board. */
  private renderBoard(): void {
    const board = this.board;
    if (!board) {
      this.headingEl.textContent = STRINGS.timeLeft;
      this.livesEl.textContent = "";
      this.minesEl.textContent = "";
      this.statusEl.textContent = "";
      for (const cell of this.cells) {
        cell.className = "cell";
        cell.textContent = "";
        cell.disabled = true;
      }
      return;
    }

    this.buildCells(board.width, board.height);
    this.headingEl.textContent = `${STRINGS.boardNumber(board.n)} · ${STRINGS.difficultyNames[this.difficulty]}`;
    this.livesEl.textContent = "\u2665".repeat(board.lives) + "\u2661".repeat(Math.max(0, 3 - board.lives));
    this.livesEl.title = STRINGS.livesLeft(board.lives);
    this.livesEl.setAttribute("aria-label", STRINGS.livesLeft(board.lives));
    const left = Math.max(0, board.mines - board.flags.length - board.boom.length);
    this.minesEl.textContent = `\u{1F4A3} ${left}`;
    this.minesEl.title = STRINGS.minesLeft(left);
    this.minesEl.setAttribute("aria-label", STRINGS.minesLeft(left));
    this.statusEl.textContent =
      board.phase === "cleared" ? STRINGS.clearedBanner : board.phase === "lost" ? STRINGS.lostBanner : "";
    this.statusEl.className = `stage-status${board.phase === "cleared" ? " is-cleared" : ""}${board.phase === "lost" ? " is-lost" : ""}`;

    const open = new Map(board.open.map(([cell, count, slot]) => [cell, [count, slot] as const]));
    const flags = new Map(board.flags);
    const boom = new Map(board.boom);
    const field = new Set(board.field);
    const acting = board.phase === "open";
    this.cells.forEach((button, cell) => {
      const x = (cell % board.width) + 1;
      const y = Math.floor(cell / board.width) + 1;
      const opened = open.get(cell);
      let className = "cell";
      let text = "";
      let label = STRINGS.cellClosed(x, y);
      let by: number | undefined;
      if (opened) {
        const [count, slot] = opened;
        className = `cell cell-open n${count}`;
        text = count > 0 ? String(count) : "";
        label = STRINGS.cellOpen(x, y, count);
        by = slot;
      } else if (boom.has(cell)) {
        className = "cell cell-boom";
        text = "\u{1F4A5}";
        label = STRINGS.cellMine(x, y);
        by = boom.get(cell);
      } else if (flags.has(cell)) {
        // Once the board is over, a flag on a mine was right and a flag elsewhere was not.
        className = !acting && !field.has(cell) ? "cell cell-flag cell-wrong" : "cell cell-flag";
        text = "\u{1F6A9}";
        label = STRINGS.cellFlagged(x, y);
        by = flags.get(cell);
      } else if (field.has(cell)) {
        className = "cell cell-mine";
        text = "\u{1F4A3}";
        label = STRINGS.cellMine(x, y);
      }
      if (button.className !== className) button.className = className;
      if (button.textContent !== text) button.textContent = text;
      button.setAttribute("aria-label", label);
      button.disabled = !acting || opened !== undefined || boom.has(cell);
      // A tint of the color of whoever opened, flagged or set off the cell.
      if (by === undefined) button.style.removeProperty("--by");
      else button.style.setProperty("--by", PLAYER_COLORS[by % PLAYER_COLORS.length]!);
    });
    this.boardEl.classList.toggle("is-over", !acting);
  }

  /** Team totals and what each person added, best first. */
  private renderTeam(): void {
    const board = this.board;
    const finished = this.status === "finished" ? this.summary : null;
    const score = board ? board.score : finished ? finished.score : 0;
    const cleared = board ? board.cleared : finished ? finished.cleared : 0;
    const lost = board ? board.lost : finished ? finished.lost : 0;
    this.teamScoreEl.textContent = String(score);
    this.teamStatsEl.textContent = `${STRINGS.clearedCount(cleared)} · ${STRINGS.lostCount(lost)}`;

    this.playersEl.replaceChildren(
      ...rank([...this.players.values()]).map((p) => {
        const row = h(
          "li",
          { class: p.online ? "player" : "player player-offline" },
          h("span", { class: "avatar", "aria-hidden": "true" }, initial(p.name)),
          h(
            "span",
            { class: "player-name" },
            p.name,
            p.id === this.you ? h("span", { class: "tag" }, STRINGS.you) : null,
            p.online ? null : h("span", { class: "tag" }, STRINGS.offline),
          ),
          h("span", { class: "player-score", title: STRINGS.points(p.score) }, String(p.score)),
        );
        row.style.setProperty("--player", PLAYER_COLORS[p.slot % PLAYER_COLORS.length]!);
        return row;
      }),
    );
  }

  /** Round clock, from the local deadline. */
  private refresh(): void {
    this.setClock(this.deadline - Date.now());
  }

  private onlineCount(): number {
    return [...this.players.values()].filter((p) => p.online).length;
  }

  private setClock(ms: number): void {
    this.clockEl.textContent = formatClock(ms);
    const running = this.status === "running";
    this.clockEl.classList.toggle("clock-running", running);
    this.clockEl.classList.toggle("clock-final", running && ms <= CLOCK_FINAL_MS);
    // Hue 135 (green) → 0 (red), passing through yellow and orange on the way.
    const left = Math.min(1, Math.max(0, (ms - CLOCK_FINAL_MS) / (CLOCK_WARN_MS - CLOCK_FINAL_MS)));
    this.clockEl.style.setProperty("--clock-hue", String(Math.round(CLOCK_GREEN_HUE * left)));
  }

  private toast(text: string, sticky = false): void {
    clearTimeout(this.toastTimer);
    this.toastEl.textContent = text;
    this.toastEl.hidden = false;
    if (!sticky) this.toastTimer = setTimeout(() => this.hideToast(), TOAST_MS);
  }

  private hideToast(): void {
    clearTimeout(this.toastTimer);
    this.toastEl.hidden = true;
  }
}

/** Clipboard API where available, with the legacy selection path as a fallback. */
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the legacy path.
  }
  const area = h("textarea", { readonly: true, class: "visually-hidden" });
  area.value = text;
  document.body.append(area);
  area.select();
  let ok = false;
  try {
    ok = document.execCommand("copy");
  } catch {
    ok = false;
  }
  area.remove();
  return ok;
}

function initial(name: string): string {
  return [...name][0]?.toUpperCase() ?? "?";
}

/**
 * Invite links use the canonical domain, except when running locally or on
 * *.workers.dev, where the canonical link would point to a different deployment.
 */
function inviteUrl(code: string): string {
  const host = location.hostname;
  const isLocal =
    host === "localhost" ||
    /^(127\.|10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(host) ||
    host.endsWith(".workers.dev");
  return `${isLocal ? location.origin : CANONICAL_ORIGIN}${BASE_PATH}/r/${code}`;
}
